import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { portalApi, getPortalView, setPortalView } from "@/lib/portalApi";
import { toast } from "sonner";

const WARN_SECONDS = 120;

/** Validates returnTo as a same-origin internal staff route.
 * Rejects external URLs and /portal/* paths (would re-enter portal context as staff).
 * Falls back to the previewed client's workspace, then CRM root. */
function _safeReturn(url, clientId) {
  const fallback = clientId ? `/client-workspace/${clientId}` : "/";
  if (!url) return fallback;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return fallback;
    const internal = parsed.pathname + parsed.search + parsed.hash;
    if (internal.startsWith("/portal")) return fallback; // never return to portal context
    return internal;
  } catch {
    return fallback;
  }
}

function fmt(sec) {
  if (sec == null || sec < 0) sec = 0;
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Persistent, high-visibility banner shown for the ENTIRE Portal View session.
// Dual identity is always explicit: administrator (actor) viewing a client (subject).
export default function PortalViewBanner({ view }) {
  const stored = getPortalView();
  const sid = view?.session_id || stored?.session_id;
  const mode = view?.mode || stored?.mode;
  const interactive = mode === "interactive";

  const [deadline, setDeadline] = useState(stored?.deadline_ms || null);
  const [remaining, setRemaining] = useState(null);
  const [busy, setBusy] = useState("");
  const warnedRef = useRef(false);
  const endingRef = useRef(false);

  const persistDeadline = useCallback((absIso, lastIso, idleSec) => {
    const abs = absIso ? new Date(absIso).getTime() : Infinity;
    const idle = lastIso ? new Date(lastIso).getTime() + (idleSec || 900) * 1000 : Infinity;
    const dl = Math.min(abs, idle);
    setDeadline(dl);
    const cur = getPortalView();
    if (cur) setPortalView({ ...cur, deadline_ms: dl });
    return dl;
  }, []);

  const doEnd = useCallback(async (reasonMsg) => {
    if (endingRef.current) {
      // Session already ending (e.g. from a previous extend-401 or timer expiry).
      // Ensure navigation fires regardless — the user must not be stranded at /portal.
      const v = getPortalView();
      window.location.replace(_safeReturn(v?.returnTo, v?.client_id));
      return;
    }
    endingRef.current = true;
    const view = getPortalView(); // capture before clearing — returnTo and client_id live here
    try { if (sid) await api.post(`/portal-view/sessions/${sid}/end`); } catch { /* already gone */ }
    setPortalView(null);
    if (reasonMsg) toast.message(reasonMsg);
    // Return staff to originating workspace; validated fallback chain: returnTo → /client-workspace/{id} → /
    window.location.replace(_safeReturn(view?.returnTo, view?.client_id));
  }, [sid]);

  // Poll the (non-activity) context endpoint to refresh true expiry without touching the idle timer.
  const refresh = useCallback(async () => {
    try {
      const { data } = await portalApi.get("/portal-view/context");
      persistDeadline(data.absolute_expires_at, data.last_activity_at, data.idle_seconds);
    } catch (e) {
      if (e?.response?.status === 401) doEnd("Portal View session ended.");
    }
  }, [persistDeadline, doEnd]);

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, 20000);
    return () => clearInterval(poll);
  }, [refresh]);

  // Local 1s countdown.
  useEffect(() => {
    const t = setInterval(() => {
      if (!deadline) return;
      const rem = Math.round((deadline - Date.now()) / 1000);
      setRemaining(rem);
      if (rem <= WARN_SECONDS && rem > 0 && !warnedRef.current) {
        warnedRef.current = true;
        toast.warning(`Portal View expires in about ${Math.ceil(rem / 60)} min. Extend to keep working.`);
      }
      if (rem > WARN_SECONDS) warnedRef.current = false;
      if (rem <= 0) doEnd("Portal View session expired.");
    }, 1000);
    return () => clearInterval(t);
  }, [deadline, doEnd]);

  const extend = async () => {
    setBusy("extend");
    try {
      const { data } = await api.post(`/portal-view/sessions/${sid}/extend`);
      const cur = getPortalView();
      if (cur) setPortalView({ ...cur, token: data.token });
      persistDeadline(data.absolute_expires_at, data.last_activity_at, data.idle_seconds);
      warnedRef.current = false;
      toast.success("Portal View extended by 15 minutes.");
    } catch (e) {
      if (e?.response?.status === 401) doEnd("Your staff session expired — Portal View ended.");
      else toast.error("Could not extend the session.");
    } finally { setBusy(""); }
  };

  const exit = async () => { setBusy("exit"); await doEnd("Exited Portal View."); };

  const subject = view?.subject?.name || stored?.subject?.name || "client";
  const actor = view?.actor?.name || stored?.actor?.name || "Administrator";
  const warn = remaining != null && remaining <= WARN_SECONDS;
  const tone = interactive
    ? "bg-rose-700 border-rose-900"
    : "bg-amber-600 border-amber-800";

  return (
    <div
      data-testid="portal-view-banner"
      className={`sticky top-0 z-[60] w-full text-white border-b ${warn ? "bg-red-700 border-red-900 animate-pulse" : tone}`}
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          {interactive ? <Icons.PenLine className="h-4 w-4" /> : <Icons.Eye className="h-4 w-4" />}
          {interactive ? "INTERACTIVE PORTAL VIEW" : "READ-ONLY PORTAL PREVIEW"}
        </span>
        <span className="hidden sm:inline text-white/85" data-testid="portal-view-identity">
          <strong>{actor}</strong> viewing <strong>{subject}</strong>&rsquo;s portal
          {!interactive && " — no changes can be made"}
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 font-mono tabular-nums" data-testid="portal-view-timer">
          <Icons.Clock className="h-4 w-4" /> {fmt(remaining)}
        </span>
        <button
          onClick={extend} disabled={busy === "extend"}
          data-testid="portal-view-extend"
          className="inline-flex items-center gap-1 rounded-md bg-white/15 hover:bg-white/25 px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50"
        >
          {busy === "extend" ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.TimerReset className="h-3.5 w-3.5" />}
          Extend 15m
        </button>
        <button
          onClick={exit} disabled={busy === "exit"}
          data-testid="portal-view-exit"
          className="inline-flex items-center gap-1 rounded-md bg-white text-slate-900 hover:bg-slate-100 px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50"
        >
          {busy === "exit" ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.LogOut className="h-3.5 w-3.5" />}
          Exit
        </button>
      </div>
    </div>
  );
}
